import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Home, FileText, Expand, Minimize2 } from 'lucide-react';

interface Page {
  id: string;
  title: string;
  parentPageId: string | null;
  isHomepage: boolean;
  tileCount?: number;
}

interface PageNode extends Page {
  children: PageNode[];
  level: number;
}

interface HierarchicalPageSelectorProps {
  pages: Page[];
  selectedPageId: string;
  currentPageId: string;
  onSelectPage: (pageId: string) => void;
  autoExpandSelected?: boolean;
}

export default function HierarchicalPageSelector({ 
  pages, 
  selectedPageId, 
  currentPageId,
  onSelectPage,
  autoExpandSelected = true,
}: HierarchicalPageSelectorProps) {
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());

  // Auto-expand pages that contain the selected page
  React.useEffect(() => {
    if (!autoExpandSelected) return;
    if (selectedPageId && pages) {
      const expandPath = (pageId: string, visited = new Set<string>()): string[] => {
        if (visited.has(pageId)) return []; // Prevent infinite loops
        visited.add(pageId);
        
        const page = pages.find(p => p.id === pageId);
        if (!page || !page.parentPageId) return [];
        
        return [page.parentPageId, ...expandPath(page.parentPageId, visited)];
      };

      const pathToExpand = expandPath(selectedPageId);
      if (pathToExpand.length > 0) {
        setExpandedPages(prev => new Set([...prev, ...pathToExpand]));
      }
    }
  }, [selectedPageId, pages, autoExpandSelected]);

  // Transform flat page list into hierarchical tree structure
  const pageTree = useMemo(() => {
    if (!pages) return [];

    // Create a map for quick lookup
    const pageMap = new Map<string, PageNode>();
    
    // Initialize all pages as nodes
    pages.forEach(page => {
      pageMap.set(page.id, {
        ...page,
        children: [],
        level: 0
      });
    });

    // Build the tree structure
    const rootPages: PageNode[] = [];
    
    pages.forEach(page => {
      const node = pageMap.get(page.id)!;
      
      if (page.parentPageId && pageMap.has(page.parentPageId)) {
        // This page has a parent
        const parent = pageMap.get(page.parentPageId)!;
        parent.children.push(node);
      } else {
        // This is a root page (homepage or orphaned page)
        rootPages.push(node);
      }
    });

    // Recursively calculate levels after tree structure is built
    const calculateLevels = (nodes: PageNode[], level: number = 0) => {
      nodes.forEach(node => {
        node.level = level;
        calculateLevels(node.children, level + 1);
      });
    };
    
    calculateLevels(rootPages);

    // Sort children by title
    const sortChildren = (nodes: PageNode[]) => {
      nodes.sort((a, b) => a.title.localeCompare(b.title));
      nodes.forEach(node => sortChildren(node.children));
    };

    sortChildren(rootPages);
    return rootPages;
  }, [pages]);

  const toggleExpanded = (pageId: string) => {
    setExpandedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allPageIds = pages.map(page => page.id);
    setExpandedPages(new Set(allPageIds));
  };

  const collapseAll = () => {
    setExpandedPages(new Set());
  };

  const renderPageNode = (node: PageNode): React.ReactNode => {
    const isExpanded = expandedPages.has(node.id);
    const hasChildren = node.children.length > 0;
    const isCurrentPage = node.id === currentPageId;
    const isSelected = node.id === selectedPageId;

    return (
      <div key={node.id} className="select-none">
        {/* Page Row */}
        <div 
          className={`flex items-center py-2 px-2 rounded-md hover:bg-gray-50 ${
            isSelected ? 'bg-blue-50 border border-blue-200' : ''
          } ${isCurrentPage ? 'opacity-50' : ''}`}
          style={{ paddingLeft: `${node.level * 20 + 8}px` }}
        >
          {/* Expand/Collapse Button */}
          <div className="w-5 h-5 flex items-center justify-center mr-2">
            {hasChildren ? (
              <button
                onClick={() => toggleExpanded(node.id)}
                className="p-0.5 rounded hover:bg-gray-200 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 text-gray-600" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-3 h-3" /> // Spacer for alignment
            )}
          </div>

          {/* Page Icon */}
          <div className="w-4 h-4 flex items-center justify-center mr-2">
            {node.isHomepage ? (
              <Home className="h-3 w-3 text-blue-600" />
            ) : (
              <FileText className="h-3 w-3 text-gray-500" />
            )}
          </div>

          {/* Page Title */}
          <div className="flex-1 min-w-0">
            <span className={`text-sm truncate ${isCurrentPage ? 'text-gray-400' : 'text-gray-900'}`}>
              {node.title}
              {node.isHomepage && ' (Homepage)'}
              {isCurrentPage && ' (Current)'}
            </span>
            {node.tileCount !== undefined && (
              <span className="text-xs text-gray-500 ml-1">
                ({node.tileCount} tiles)
              </span>
            )}
          </div>

          {/* Select Button */}
          <button
            onClick={() => onSelectPage(node.id)}
            disabled={isCurrentPage}
            className={`ml-2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              isSelected 
                ? 'bg-blue-600 text-white' 
                : isCurrentPage
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            }`}
          >
            {isSelected ? 'Selected' : isCurrentPage ? 'Current' : 'Select'}
          </button>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderPageNode(child))}
          </div>
        )}
      </div>
    );
  };

  if (!pages || pages.length === 0) {
    return (
      <div className="text-sm text-gray-500 p-4 text-center">
        No pages available
      </div>
    );
  }

  const hasAnyChildren = pageTree.some(node => node.children.length > 0);

  return (
    <div className="border border-gray-200 rounded-md bg-white">
      {/* Header with expand/collapse controls */}
      {hasAnyChildren && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
          <span className="text-xs font-medium text-gray-600">
            {pages.length} page{pages.length !== 1 ? 's' : ''} available
          </span>
          <div className="flex items-center space-x-1">
            <button
              onClick={expandAll}
              className="p-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
              title="Expand all"
            >
              <Expand className="h-3 w-3" />
            </button>
            <button
              onClick={collapseAll}
              className="p-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
              title="Collapse all"
            >
              <Minimize2 className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      
      {/* Page tree */}
      <div className="max-h-80 overflow-y-auto p-2 space-y-1">
        {pageTree.map(node => renderPageNode(node))}
      </div>
    </div>
  );
}
